/**
 * AI Command Palette Component
 * 
 * Global command palette (Cmd/Ctrl + K) for accessing all AI capabilities.
 * Features fuzzy search, keyboard navigation, and quick execution.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiApi, AICapability } from '../api';
import { useAuth } from '../../../contexts/AuthContext';

interface Command extends AICapability {
  type: 'capability' | 'navigation';
  route?: string;
  action?: () => void;
  icon?: string;
}

interface AICommandPaletteProps {
  className?: string;
}

const AICommandPalette: React.FC<AICommandPaletteProps> = ({ className = '' }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [commands, setCommands] = useState<Command[]>([]);
  const [filteredCommands, setFilteredCommands] = useState<Command[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isExecuting, setIsExecuting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Global keyboard shortcut: Cmd/Ctrl + K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
      
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Load commands when opened
  useEffect(() => {
    if (isOpen && user) {
      loadCommands();
      // Focus input after a brief delay
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, user]);

  // Filter commands based on search
  useEffect(() => {
    if (!search.trim()) {
      setFilteredCommands(commands);
      setSelectedIndex(0);
      return;
    }

    const searchLower = search.toLowerCase();
    const filtered = commands.filter((cmd) => {
      const nameMatch = cmd.task_name.toLowerCase().includes(searchLower);
      const descMatch = cmd.description.toLowerCase().includes(searchLower);
      const keywordMatch = cmd.keywords?.some((kw) => kw.toLowerCase().includes(searchLower));
      return nameMatch || descMatch || keywordMatch;
    });

    setFilteredCommands(filtered);
    setSelectedIndex(0);
  }, [search, commands]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, filteredCommands.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredCommands[selectedIndex]) {
          executeCommand(filteredCommands[selectedIndex]);
        }
        break;
    }
  };

  const loadCommands = async () => {
    try {
      const capabilities = await aiApi.getAvailableCapabilities();
      const navigationCommands = getNavigationCommands();
      
      const allCommands: Command[] = [
        ...capabilities.map((cap) => ({
          ...cap,
          type: 'capability' as const,
          keywords: generateKeywords(cap),
        })),
        ...navigationCommands,
      ];

      setCommands(allCommands);
      setFilteredCommands(allCommands);
    } catch (error) {
      console.error('Failed to load commands:', error);
    }
  };

  const getNavigationCommands = (): Command[] => {
    const navCommands: Command[] = [
      {
        task_name: 'Dashboard',
        description: 'Navigate to dashboard',
        type: 'navigation',
        route: '/dashboard',
        scope: 'system',
        allowed_personas: ['system_admin', 'company_admin', 'compliance_user'],
        risk_level: 'low',
        confirmation_token_required: false,
        icon: 'dashboard',
        category: 'Navigation',
        keywords: ['home', 'main', 'overview'],
      },
      {
        task_name: 'Users',
        description: 'Navigate to user management',
        type: 'navigation',
        route: '/admin/users',
        scope: 'system',
        allowed_personas: ['system_admin', 'company_admin'],
        risk_level: 'low',
        confirmation_token_required: false,
        icon: 'people',
        category: 'Navigation',
        keywords: ['users', 'accounts', 'team'],
      },
      {
        task_name: 'Compliance',
        description: 'Navigate to compliance dashboard',
        type: 'navigation',
        route: '/compliance',
        scope: 'company',
        allowed_personas: ['company_admin', 'compliance_user'],
        risk_level: 'low',
        confirmation_token_required: false,
        icon: 'check_circle',
        category: 'Navigation',
        keywords: ['compliance', 'requirements', 'controls'],
      },
    ];

    return navCommands;
  };

  const generateKeywords = (capability: AICapability): string[] => {
    const words = [
      ...capability.task_name.split('_'),
      ...capability.description.split(' '),
    ];
    return words.map((w) => w.toLowerCase()).filter((w) => w.length > 3);
  };

  const executeCommand = async (command: Command) => {
    if (isExecuting) return;

    if (command.type === 'navigation' && command.route) {
      navigate(command.route);
      setIsOpen(false);
      setSearch('');
      return;
    }

    if (command.type === 'capability') {
      setIsExecuting(true);
      try {
        const result = await aiApi.executeCapability(command.task_name, {
          companyId: user?.company_id,
        });

        if (result.response_type === 'executed') {
          showNotification('success', 'Command executed successfully');
        } else if (result.response_type === 'confirmation_required') {
          showNotification('info', 'Check AI Assistant for confirmation');
        }

        setIsOpen(false);
        setSearch('');
      } catch (error: any) {
        console.error('Failed to execute command:', error);
        showNotification('error', error.message || 'Execution failed');
      } finally {
        setIsExecuting(false);
      }
    }

    if (command.action) {
      command.action();
      setIsOpen(false);
      setSearch('');
    }
  };

  const showNotification = (type: 'success' | 'error' | 'info', message: string) => {
    const toast = document.createElement('div');
    toast.className = `fixed bottom-4 right-4 p-4 rounded-lg shadow-lg z-50 ${
      type === 'success' ? 'bg-green-500' : type === 'error' ? 'bg-red-500' : 'bg-blue-500'
    } text-white`;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  };

  const getCategoryIcon = (command: Command) => {
    if (command.icon) return command.icon;
    
    switch (command.category) {
      case 'Company':
        return 'business';
      case 'User':
        return 'person';
      case 'Compliance':
        return 'check_circle';
      case 'Framework':
        return 'description';
      case 'Navigation':
        return 'navigation';
      default:
        return 'bolt';
    }
  };

  const getRiskBadge = (riskLevel: string) => {
    switch (riskLevel) {
      case 'high':
        return <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">High Risk</span>;
      case 'medium':
        return <span className="text-xs px-2 py-0.5 rounded bg-yellow-100 text-yellow-700">Medium</span>;
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={() => setIsOpen(false)}
      />

      {/* Command Palette */}
      <div className={`fixed top-1/4 left-1/2 transform -translate-x-1/2 w-full max-w-2xl bg-white dark:bg-gray-800 rounded-lg shadow-2xl z-50 ${className}`}>
        {/* Search Input */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a command or search..."
              className="flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-white text-lg"
              disabled={isExecuting}
            />
            <kbd className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100 dark:bg-gray-700 rounded">
              ESC
            </kbd>
          </div>
        </div>

        {/* Command List */}
        <div className="max-h-96 overflow-y-auto">
          {filteredCommands.length === 0 ? (
            <div className="p-8 text-gray-500">
              No commands found
            </div>
          ) : (
            <div className="p-2">
              {filteredCommands.map((cmd, idx) => (
                <button
                  key={`${cmd.type}-${cmd.task_name}`}
                  onClick={() => executeCommand(cmd)}
                  disabled={isExecuting}
                  className={`w-full text-left p-3 rounded-lg mb-1 transition-all ${
                    idx === selectedIndex
                      ? 'bg-purple-100 dark:bg-purple-900 border border-purple-300 dark:border-purple-700'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {/* Icon */}
                    <div className="flex-shrink-0 w-8 h-8 rounded bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                      <span className="material-icons text-sm text-purple-600 dark:text-purple-400">
                        {getCategoryIcon(cmd)}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {cmd.task_name.replace(/_/g, ' ')}
                        </span>
                        {getRiskBadge(cmd.risk_level)}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        {cmd.description}
                      </div>
                      {cmd.category && (
                        <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          {cmd.category}
                        </div>
                      )}
                    </div>

                    {/* Enter hint */}
                    {idx === selectedIndex && (
                      <kbd className="flex-shrink-0 px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100 dark:bg-gray-700 rounded">
                        ↵
                      </kbd>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">↑</kbd>
              <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">↓</kbd>
              <span>Navigate</span>
            </div>
            <div className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">↵</kbd>
              <span>Execute</span>
            </div>
          </div>
          <div>{filteredCommands.length} commands</div>
        </div>
      </div>
    </>
  );
};

export default AICommandPalette;
